import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import axios from 'axios';
import { 
  Wallet,
  Users,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  ArrowUpRight,
  ArrowDownLeft,
  Building2,
  CreditCard,
  Plus,
  Loader2,
  Search,
  Filter,
  MapPin
} from 'lucide-react';

const DirectorWalletManagement = () => {
  const [directors, setDirectors] = useState([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [summary, setSummary] = useState(null);
  const [withdrawalSummary, setWithdrawalSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);
  const [selectedDirector, setSelectedDirector] = useState(null);
  const [addFundsAmount, setAddFundsAmount] = useState('');
  const [addFundsDescription, setAddFundsDescription] = useState('');
  const [addingFunds, setAddingFunds] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingId, setRejectingId] = useState(null);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchDirectorWallets();
    fetchWithdrawalRequests();
  }, []);

  const fetchDirectorWallets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api/admin/director-wallets`, {
        withCredentials: true
      });
      setDirectors(response.data.directors || []);
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Error fetching director wallets:', error);
      toast.error("Failed to fetch director wallets.");
    } finally {
      setLoading(false);
    }
  };

  const fetchWithdrawalRequests = async () => {
    try {
      setLoadingRequests(true);
      const response = await axios.get(`${API_BASE}/api/admin/withdrawal-requests`, {
        withCredentials: true
      });
      setWithdrawalRequests(response.data.requests || []);
      setWithdrawalSummary(response.data.summary);
    } catch (error) {
      console.error('Error fetching withdrawal requests:', error);
      toast.error("Failed to fetch withdrawal requests.");
    } finally {
      setLoadingRequests(false);
    }
  };

  const approveWithdrawal = async (requestId) => {
    try {
      setProcessingId(requestId);
      const response = await axios.post(
        `${API_BASE}/api/admin/withdrawal-requests/${requestId}/approve`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      fetchWithdrawalRequests();
      fetchDirectorWallets();
    } catch (error) {
      console.error('Error approving withdrawal:', error);
      toast.error(error.response?.data?.detail || "Failed to approve withdrawal.");
    } finally {
      setProcessingId(null);
    }
  };

  const rejectWithdrawal = async (requestId) => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }
    
    try {
      setProcessingId(requestId);
      const response = await axios.post(
        `${API_BASE}/api/admin/withdrawal-requests/${requestId}/reject`,
        { reason: rejectReason },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setRejectingId(null);
      setRejectReason('');
      fetchWithdrawalRequests();
      fetchDirectorWallets();
    } catch (error) {
      console.error('Error rejecting withdrawal:', error);
      toast.error(error.response?.data?.detail || "Failed to reject withdrawal.");
    } finally {
      setProcessingId(null);
    }
  };

  const addFunds = async () => {
    const amount = parseFloat(addFundsAmount);
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    
    try {
      setAddingFunds(true);
      const response = await axios.post(
        `${API_BASE}/api/admin/director-wallets/${selectedDirector.director_id}/add-funds`,
        { 
          amount, 
          description: addFundsDescription || 'Admin credit' 
        },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setShowAddFundsModal(false);
      setAddFundsAmount('');
      setAddFundsDescription('');
      setSelectedDirector(null);
      fetchDirectorWallets();
    } catch (error) {
      console.error('Error adding funds:', error);
      toast.error(error.response?.data?.detail || "Failed to add funds.");
    } finally {
      setAddingFunds(false);
    }
  };

  const filteredRequests = withdrawalRequests.filter(req => {
    const matchesSearch = 
      req.director_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.district_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.mobile_money_number?.includes(searchTerm);
    
    const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-800"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-blue-800">Total Directors</CardTitle>
            <Users className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">
              {summary?.total_directors || 0}
            </div>
            <p className="text-xs text-blue-600">With wallet accounts</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-emerald-800">Total Balance</CardTitle>
            <Wallet className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-700">
              GH₵ {summary?.total_balance?.toLocaleString() || '0'}
            </div>
            <p className="text-xs text-emerald-600">Across all wallets</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-amber-800">Pending Withdrawals</CardTitle>
            <Clock className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-700">
              {withdrawalSummary?.pending_count || 0}
            </div>
            <p className="text-xs text-amber-600">
              GH₵ {withdrawalSummary?.pending_amount?.toLocaleString() || '0'} total
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-purple-800">Total Withdrawn</CardTitle>
            <ArrowUpRight className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">
              GH₵ {summary?.total_withdrawn?.toLocaleString() || '0'}
            </div>
            <p className="text-xs text-purple-600">All time</p>
          </CardContent>
        </Card>
      </div>

      {/* Withdrawal Requests Section */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-amber-600" />
                Withdrawal Requests
              </CardTitle>
              <CardDescription>
                Process director withdrawal requests
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-48"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border rounded-md px-3 py-2 text-sm"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  fetchWithdrawalRequests();
                  fetchDirectorWallets();
                }}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loadingRequests ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : filteredRequests.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Director</TableHead>
                  <TableHead>District</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Mobile Money</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map((req) => (
                  <TableRow key={req.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{req.director_name}</p>
                        <p className="text-xs text-gray-500">{req.director_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-gray-400" />
                        {req.district_name || 'N/A'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-semibold text-emerald-600">
                        GH₵ {req.amount?.toLocaleString()}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="font-medium">{req.mobile_money_network}</p>
                        <p className="text-gray-500">{req.mobile_money_number}</p>
                        <p className="text-xs text-gray-400">{req.account_name}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-500">
                        {new Date(req.created_at).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(req.status)}
                      {req.rejection_reason && (
                        <p className="text-xs text-red-500 mt-1">{req.rejection_reason}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {req.status === 'pending' && (
                        <div className="flex flex-col gap-2">
                          {rejectingId === req.id ? (
                            <div className="space-y-2">
                              <Textarea
                                placeholder="Reason for rejection..."
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                className="text-sm"
                                rows={2}
                              />
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setRejectingId(null);
                                    setRejectReason('');
                                  }}
                                  className="text-xs"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => rejectWithdrawal(req.id)}
                                  disabled={processingId === req.id}
                                  className="text-xs bg-red-600 hover:bg-red-700"
                                >
                                  {processingId === req.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : 'Confirm'}
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex gap-1 justify-end">
                              <Button
                                size="sm"
                                onClick={() => approveWithdrawal(req.id)}
                                disabled={processingId === req.id}
                                className="text-xs bg-green-600 hover:bg-green-700"
                              >
                                {processingId === req.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <><CheckCircle className="h-3 w-3 mr-1" />Approve</>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setRejectingId(req.id)}
                                className="text-xs text-red-600 border-red-300 hover:bg-red-50"
                              >
                                <XCircle className="h-3 w-3 mr-1" />Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <CreditCard className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No withdrawal requests found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Director Wallets Section */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="h-5 w-5 text-blue-600" />
                Director Wallets
              </CardTitle>
              <CardDescription>
                View and manage all director wallet balances
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : directors.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Director</TableHead>
                  <TableHead>District</TableHead>
                  <TableHead>Balance</TableHead>
                  <TableHead>Total Earned</TableHead>
                  <TableHead>Total Withdrawn</TableHead>
                  <TableHead>Mobile Money</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {directors.map((dir) => (
                  <TableRow key={dir.director_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{dir.director_name}</p>
                        <p className="text-xs text-gray-500">{dir.director_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Building2 className="h-3 w-3 text-gray-400" />
                        <span>{dir.district_name || 'N/A'}</span>
                      </div>
                      {dir.region && (
                        <p className="text-xs text-gray-400">{dir.region}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-semibold text-emerald-600">
                        GH₵ {dir.wallet?.balance?.toLocaleString() || '0'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-blue-600">
                        GH₵ {dir.wallet?.total_earned?.toLocaleString() || '0'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-purple-600">
                        GH₵ {dir.wallet?.total_withdrawn?.toLocaleString() || '0'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {dir.wallet?.mobile_money_number ? (
                        <div className="text-sm">
                          <p className="font-medium">{dir.wallet.mobile_money_network}</p>
                          <p className="text-gray-500">{dir.wallet.mobile_money_number}</p>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">Not set</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedDirector(dir);
                          setShowAddFundsModal(true);
                        }}
                        className="text-xs"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Funds
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No directors found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Funds Modal */}
      {showAddFundsModal && selectedDirector && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Plus className="h-5 w-5 text-green-600" />
              Add Funds to Wallet
            </h3>
            
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{selectedDirector.director_name}</p>
                <p className="text-sm text-gray-500">{selectedDirector.district_name}</p>
                <p className="text-sm text-emerald-600 mt-1">
                  Current Balance: GH₵ {selectedDirector.wallet?.balance?.toLocaleString() || '0'}
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium">Amount (GH₵)</label>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={addFundsAmount}
                  onChange={(e) => setAddFundsAmount(e.target.value)}
                  min="1"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Description (Optional)</label>
                <Textarea
                  placeholder="e.g., Application handling fee for Term 2"
                  value={addFundsDescription}
                  onChange={(e) => setAddFundsDescription(e.target.value)}
                  rows={2}
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddFundsModal(false);
                    setSelectedDirector(null);
                    setAddFundsAmount('');
                    setAddFundsDescription('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={addFunds}
                  disabled={addingFunds}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {addingFunds ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Add Funds
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DirectorWalletManagement;
